/**
 * Global Ask AI shell — keeps the Guide drawer mounted once and exposes
 * `openAskAi()` to the header, sidebar and legacy `/ask-ai` redirects.
 *
 * Behind the `ask_ai` customer feature flag (off by default; operators switch it
 * per customer / org / tenant / user in the admin portal). No service answers
 * the assistant's `/ai/chat` endpoint yet, so an unconditional drawer offered a
 * feature where every question failed. Gated HERE, once, rather than at each
 * door: while the flag is off the drawer is not mounted, `openAskAi` does
 * nothing, and `enabled` tells the doors to stay out of sight.
 */
import * as React from "react";
import { useFeatureFlag } from "@authe/ui-core/flags";
import { FEATURE_FLAG } from "@authe/fe-commons/features";
import { AskAiDrawer } from "@/components/shell/AskAiDrawer";

interface AskAiContextValue {
  /** Whether Ask AI is rolled out to this session. Render no door when false. */
  enabled: boolean;
  openAskAi: () => void;
}

const AskAiContext = React.createContext<AskAiContextValue | null>(null);

export function AskAiProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const enabled = useFeatureFlag(FEATURE_FLAG.askAi);
  const [open, setOpen] = React.useState(false);
  const openAskAi = React.useCallback(() => {
    if (enabled) setOpen(true);
  }, [enabled]);

  // Switched off mid-session (an operator, or a tenancy switch): close, so
  // switching it back on later does not reopen a drawer nobody asked for.
  React.useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  const value = React.useMemo<AskAiContextValue>(() => ({ enabled, openAskAi }), [enabled, openAskAi]);

  return (
    <AskAiContext.Provider value={value}>
      {children}
      {enabled ? <AskAiDrawer open={open} onOpenChange={setOpen} /> : null}
    </AskAiContext.Provider>
  );
}

export function useAskAi(): AskAiContextValue {
  const ctx = React.useContext(AskAiContext);
  if (!ctx) {
    throw new Error("useAskAi must be used within AskAiProvider");
  }
  return ctx;
}
